import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Settings, Mic, Volume2, Languages, Radio, Headphones,
} from "lucide-react";
import TolokyConsentDialog from "./TolokyConsentDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const VOICE_OPTIONS = [
  { value: "ava", label: "Ava (Female)" },
  { value: "andrew", label: "Andrew (Male)" },
  { value: "fable", label: "Fable (Neutral)" },
];

const MODE_OPTIONS = [
  { value: "consecutive", label: "Consecutive (turn-based)" },
  { value: "realtime", label: "Real-time (simultaneous)" },
];

export default function TolokySettingsModal({ open, onOpenChange }) {
  const [settings, setSettings] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConsent, setShowConsent] = useState(false);
  const [voiceSimStatus, setVoiceSimStatus] = useState(null);

  useEffect(() => {
    if (open) {
      fetchSettings();
      fetchLanguages();
    }
  }, [open]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/comms/toloky/settings`, {
        headers: getHeaders(), withCredentials: true,
      });
      setSettings(res.data);
    } catch {}
    setLoading(false);
  };

  const fetchLanguages = async () => {
    try {
      const res = await axios.get(`${API}/comms/toloky/languages`, {
        headers: getHeaders(), withCredentials: true,
      });
      setLanguages(res.data.languages || []);
    } catch {}
  };

  const updateSetting = async (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    try {
      await axios.put(`${API}/comms/toloky/settings`, { [key]: value }, {
        headers: getHeaders(), withCredentials: true,
      });
    } catch {
      fetchSettings();
    }
  };

  const handleVoiceSimToggle = async (enabled) => {
    if (enabled && (!settings?.voice_simulation_consent)) {
      // Check if voice simulation is allowed by org policy
      try {
        const statusRes = await axios.get(`${API}/comms/toloky/voice-simulation/status`, {
          headers: getHeaders(), withCredentials: true,
        });
        setVoiceSimStatus(statusRes.data);
        if (statusRes.data.allowed === false && statusRes.data.reason === "Consent required") {
          setShowConsent(true);
          return;
        }
      } catch {}
    }
    updateSetting("voice_simulation_enabled", enabled);
  };

  if (!settings) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-white sm:max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                <Settings className="w-4 h-4 text-violet-600" />
              </div>
              Toloky Settings
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* Language Selection */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Languages className="w-3 h-3" /> Language
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">I speak</Label>
                  <Select
                    value={settings.source_language || "auto"}
                    onValueChange={(v) => updateSetting("source_language", v === "auto" ? "" : v)}
                  >
                    <SelectTrigger className="h-9 text-sm rounded-none">
                      <SelectValue placeholder="Auto-detect" />
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-none max-h-60">
                      <SelectItem value="auto">Auto-detect</SelectItem>
                      {languages.map((l) => (
                        <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">I want to hear</Label>
                  <Select
                    value={settings.target_language}
                    onValueChange={(v) => updateSetting("target_language", v)}
                  >
                    <SelectTrigger className="h-9 text-sm rounded-none">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-none max-h-60">
                      {languages.map((l) => (
                        <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Voice Simulation */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Mic className="w-3 h-3" /> Voice
              </h4>
              <div className="bg-slate-50 border border-slate-200 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Simulate my voice</p>
                    <p className="text-[11px] text-slate-500">
                      Mimics your tone and style in translation
                    </p>
                  </div>
                  <Switch
                    checked={settings.voice_simulation_enabled}
                    onCheckedChange={handleVoiceSimToggle}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Default voice</Label>
                  <Select
                    value={settings.default_voice}
                    onValueChange={(v) => updateSetting("default_voice", v)}
                  >
                    <SelectTrigger className="h-9 text-sm rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-none">
                      {VOICE_OPTIONS.map((v) => (
                        <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Interpretation Mode */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Radio className="w-3 h-3" /> Mode
              </h4>
              <Select
                value={settings.interpretation_mode}
                onValueChange={(v) => updateSetting("interpretation_mode", v)}
              >
                <SelectTrigger className="h-9 text-sm rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white rounded-none">
                  {MODE_OPTIONS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Volume Controls */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Headphones className="w-3 h-3" /> Audio Balance
              </h4>
              <div className="space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 flex items-center gap-1">
                      <Volume2 className="w-3 h-3" /> Interpreter volume
                    </span>
                    <span className="text-slate-800 font-mono">{settings.interpreter_volume}%</span>
                  </div>
                  <Slider
                    value={[settings.interpreter_volume]}
                    onValueChange={([v]) => updateSetting("interpreter_volume", v)}
                    max={100}
                    step={1}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 flex items-center gap-1">
                      <Headphones className="w-3 h-3" /> Original audio
                    </span>
                    <span className="text-slate-800 font-mono">{settings.original_volume}%</span>
                  </div>
                  <Slider
                    value={[settings.original_volume]}
                    onValueChange={([v]) => updateSetting("original_volume", v)}
                    max={100}
                    step={1}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <Button
              onClick={() => onOpenChange(false)}
              className="w-full bg-violet-600 hover:bg-violet-700 rounded-none"
            >
              Save Preferences
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <TolokyConsentDialog
        open={showConsent}
        onOpenChange={setShowConsent}
        onComplete={(granted) => {
          if (granted) {
            updateSetting("voice_simulation_enabled", true);
          }
        }}
      />
    </>
  );
}
